// Per-thread notification delivery mode.
//
// Ported from NotificationThreadSettings.swift (iOS Stoat/E2EE). The web
// implementation mirrors the iOS spec exactly:
//
//   - `NotificationDeliveryMode` enum: 'all' | 'mentionsOnly' | 'muted' | 'none'
//   - `NotificationThreadSettingsPayload` value type: mode + optional mutedUntil + ts
//   - `kaether:notif:settings:v1:` wire-format codec
//
// IMPORTANT: mutedUntil is validated on both encode AND decode. A negative or
// non-finite mutedUntil is always rejected, regardless of how it reached the
// client (wire payload, persisted record, or direct call). This mirrors the
// iOS implementation's defensive validation.
//
// Wire prefix: `kaether:notif:settings:v1:` — never `stoat:`.

// ---------------------------------------------------------------------------
// Delivery mode
// ---------------------------------------------------------------------------

/**
 * How notifications are delivered for a given channel.
 *
 *   all           — all messages trigger a notification (default)
 *   mentionsOnly  — only messages that mention the user trigger a notification
 *   muted         — no notifications; optionally until a specific timestamp
 *   none          — all notifications suppressed unconditionally
 *
 * Mirrors iOS `NotificationDeliveryMode`.
 */
export type NotificationDeliveryMode = "all" | "mentionsOnly" | "muted" | "none";

// ---------------------------------------------------------------------------
// Payload value type
// ---------------------------------------------------------------------------

/**
 * Immutable settings payload for a single channel.
 *
 * `ts` is a millisecond epoch timestamp used as the LWW (last-writer-wins)
 * key. A strictly-later `ts` wins; ties are resolved by leaving the existing
 * record in place.
 *
 * `mutedUntil` is a millisecond epoch timestamp. Present only when
 * `mode === 'muted'`. Must be a positive, finite number. Lazily-expired mutes
 * (mutedUntil < now) are handled by the Store layer, not here.
 */
export interface NotificationThreadSettingsPayload {
  readonly mode: NotificationDeliveryMode;
  /** Defined only when mode === 'muted'. Millisecond epoch. */
  readonly mutedUntil?: number;
  /** Millisecond epoch timestamp used as the LWW key. */
  readonly ts: number;
}

// ---------------------------------------------------------------------------
// Wire-format codec
// ---------------------------------------------------------------------------

/**
 * Wire format:
 *
 *   kaether:notif:settings:v1:<json>
 *
 * where `<json>` is the serialised `WirePayload` (mode + optional mutedUntil
 * + ts). The prefix is a compile-time constant so no typo can produce a
 * `stoat:` prefix.
 */
export const NOTIF_SETTINGS_WIRE_PREFIX = "kaether:notif:settings:v1:" as const;

/** Raw JSON shape on the wire. Internal to this module. */
interface WirePayload {
  mode: NotificationDeliveryMode;
  mutedUntil?: number;
  ts: number;
}

/** Closed set of valid delivery modes for defensive decode. */
const VALID_MODES: ReadonlySet<string> = new Set([
  "all",
  "mentionsOnly",
  "muted",
  "none",
]);

/**
 * Validate that `mutedUntil` satisfies the invariant:
 *   - must be a positive, finite number
 *   - only allowed when mode === 'muted'
 *
 * Returns a string describing the violation, or `null` if valid.
 */
function validateMutedUntil(
  mode: NotificationDeliveryMode,
  mutedUntil: number | undefined,
): string | null {
  if (mutedUntil === undefined) {
    // No mutedUntil — always OK (even for 'muted'; it means no expiry).
    return null;
  }
  if (!Number.isFinite(mutedUntil) || mutedUntil < 0) {
    return `mutedUntil must be a non-negative finite number, got ${mutedUntil}`;
  }
  return null;
}

export const NotificationThreadSettingsCodec = {
  /**
   * Encode a payload to its wire-format string.
   *
   * Validates `mutedUntil` before encoding. Throws if the payload is invalid.
   */
  encode(payload: NotificationThreadSettingsPayload): string {
    const validationError = validateMutedUntil(payload.mode, payload.mutedUntil);
    if (validationError !== null) {
      throw new Error(
        `[NotificationThreadSettingsCodec.encode] ${validationError}`,
      );
    }

    const wire: WirePayload = { mode: payload.mode, ts: payload.ts };
    if (payload.mutedUntil !== undefined) {
      wire.mutedUntil = payload.mutedUntil;
    }

    return NOTIF_SETTINGS_WIRE_PREFIX + JSON.stringify(wire);
  },

  /**
   * Decode a wire-format string into a payload.
   *
   * Returns `null` if the string does not start with the correct prefix,
   * if the JSON is malformed, if the mode is not a valid delivery mode, or
   * if `mutedUntil` fails validation. Never throws.
   */
  decode(raw: string): NotificationThreadSettingsPayload | null {
    if (!raw.startsWith(NOTIF_SETTINGS_WIRE_PREFIX)) {
      return null;
    }

    const json = raw.slice(NOTIF_SETTINGS_WIRE_PREFIX.length);
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      return null;
    }

    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }

    const obj = parsed as Record<string, unknown>;

    // Validate mode
    if (typeof obj["mode"] !== "string" || !VALID_MODES.has(obj["mode"])) {
      return null;
    }
    const mode = obj["mode"] as NotificationDeliveryMode;

    // Validate ts
    if (typeof obj["ts"] !== "number" || !Number.isFinite(obj["ts"])) {
      return null;
    }
    const ts = obj["ts"] as number;

    // Validate mutedUntil (present only when mode === 'muted')
    let mutedUntil: number | undefined;
    if ("mutedUntil" in obj) {
      if (
        typeof obj["mutedUntil"] !== "number"
      ) {
        return null;
      }
      mutedUntil = obj["mutedUntil"] as number;
      const validationError = validateMutedUntil(mode, mutedUntil);
      if (validationError !== null) {
        return null;
      }
    }

    return { mode, mutedUntil, ts };
  },
} as const;
